import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { api, getToken, setToken, clearToken } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "./AuthContext";

const VoiceContext = createContext(null);
const MUTE_KEY = "gio_voice_mute";
const VOICE_KEY = "gio_voice_id";
const AUTOSPEAK_KEY = "gio_voice_autospeak";

export const VoiceProvider = ({ children }) => {
  const auth = useAuth();
  const [voices, setVoices] = useState([]);
  const [voiceId, setVoiceId] = useState(localStorage.getItem(VOICE_KEY) || "");
  const [muted, setMuted] = useState(localStorage.getItem(MUTE_KEY) === "1");
  const [autoSpeak, setAutoSpeak] = useState(localStorage.getItem(AUTOSPEAK_KEY) !== "0"); // default ON
  const [speaking, setSpeaking] = useState(false);
  const [level, setLevel] = useState(0); // 0..1 audio level for animation
  const audioRef = useRef(null);
  const audioCtxRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => { localStorage.setItem(MUTE_KEY, muted ? "1" : "0"); }, [muted]);
  useEffect(() => { localStorage.setItem(AUTOSPEAK_KEY, autoSpeak ? "1" : "0"); }, [autoSpeak]);
  useEffect(() => { if (voiceId) localStorage.setItem(VOICE_KEY, voiceId); }, [voiceId]);

  // Load voices + default on mount (only when authenticated)
  useEffect(() => {
    if (!auth?.user) return;
    (async () => {
      try {
        const [v, d] = await Promise.all([
          api.get("/api/voice/voices").catch(() => ({ data: [] })),
          api.get("/api/voice/default").catch(() => ({ data: { voice_id: "" } })),
        ]);
        setVoices(v.data || []);
        if (!voiceId && d.data?.voice_id) setVoiceId(d.data.voice_id);
      } catch (e) { /* ignore */ }
    })();
    // eslint-disable-next-line
  }, [auth?.user]);

  const stop = () => {
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch (_) {}
      audioRef.current = null;
    }
    try { if (window.speechSynthesis) window.speechSynthesis.cancel(); } catch (_) {}
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    setSpeaking(false);
    setLevel(0);
  };

  const speakBrowser = (text) => {
    // FREE primary path: browser-native SpeechSynthesis with boundary-event lipsync.
    try {
      if (!window.speechSynthesis) return false;
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      // Pick best voice — prefer Greek if text has Greek chars, else masculine English
      const isGreek = /[\u0370-\u03FF\u1F00-\u1FFF]/.test(text);
      const all = window.speechSynthesis.getVoices();
      const preferred = (isGreek
        ? all.find((v) => /el[-_]GR|el[-_]/i.test(v.lang) || /Greek|Ελλ/i.test(v.name))
        : all.find((v) => /male|onyx|daniel|alex|fred|diego|charon/i.test(v.name)))
        || all.find((v) => isGreek ? false : v.lang?.startsWith("en"))
        || all[0];
      if (preferred) u.voice = preferred;
      u.lang = preferred?.lang || (isGreek ? "el-GR" : "en-US");
      u.rate = 0.95;
      u.pitch = 0.85;
      setSpeaking(true);

      // Boundary-event lipsync: each word triggers a fresh "mouth open" pulse
      // that fades out — naturally syncs to speech tempo.
      let pulse = 0;
      let target = 0;
      const tick = () => {
        // Smoothly approach target, then decay
        pulse += (target - pulse) * 0.35;
        target *= 0.85;
        setLevel(Math.max(0, Math.min(1, pulse)));
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();

      u.onboundary = (e) => {
        if (e.name === "word" || e.name === "sentence") {
          // Pulse mouth between 0.45 and 0.85 per word for natural variability
          target = 0.5 + Math.random() * 0.35;
        }
      };
      u.onend = () => {
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        setSpeaking(false); setLevel(0);
      };
      u.onerror = () => {
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        setSpeaking(false); setLevel(0);
      };
      window.speechSynthesis.speak(u);
      return true;
    } catch (_) { return false; }
  };

  const speak = async (text) => {
    if (muted || !text || !text.trim()) return;
    stop();
    // FREE-FIRST: try browser SpeechSynthesis as primary path (no API quota).
    // Only attempt OpenAI TTS if user explicitly upgraded (voiceId set + prefer cloud).
    if (speakBrowser(text)) return;

    // Last resort: OpenAI TTS if browser doesn't support SpeechSynthesis
    try {
      const r = await api.post("/api/voice/tts", { text, voice_id: voiceId || undefined });
      const src = `data:${r.data.mime};base64,${r.data.audio_b64}`;
      const audio = new Audio(src);
      audioRef.current = audio;
      audio.addEventListener("ended", stop);
      audio.addEventListener("error", stop);
      setSpeaking(true);
      // simple level oscillator since we don't have analyser here
      let t = 0;
      const tick = () => { t += 0.2; setLevel(0.3 + 0.3 * Math.abs(Math.sin(t))); rafRef.current = requestAnimationFrame(tick); };
      tick();
      await audio.play();
    } catch (_) {
      stop();
    }
  };

  return (
    <VoiceContext.Provider value={{
      voices, voiceId, setVoiceId, muted, setMuted, autoSpeak, setAutoSpeak,
      speaking, level, speak, stop,
    }}>
      {children}
    </VoiceContext.Provider>
  );
};

export const useVoice = () => useContext(VoiceContext);
